import type { Session } from "@supabase/supabase-js";
import type { IProfileGetRes } from "@badorbad/shared";
import { create } from "zustand";
import { api } from "../lib/api";
import { supabase } from "../lib/supabase";

interface AuthState {
  session: Session | null;
  displayName: string | null;
  avatarSeed: string | null;
  loading: boolean;
  error: string | null;

  init: () => Promise<void>;
  signInAnonymously: (captchaToken?: string) => Promise<void>;
  register: (
    email: string,
    password: string,
    captchaToken?: string,
  ) => Promise<void>;
  signIn: (
    email: string,
    password: string,
    captchaToken?: string,
  ) => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string, captchaToken?: string) => Promise<void>;
  updatePassword: (password: string) => Promise<void>;
  deleteAccount: () => Promise<void>;
  updateProfile: (
    fields: { display_name?: string | null; avatar_seed?: string | null },
  ) => Promise<void>;
  fetchProfile: () => Promise<void>;
}

export const useAuth = create<AuthState>((set, get) => ({
  session: null,
  displayName: null,
  avatarSeed: null,
  loading: true,
  error: null,

  init: async () => {
    const { data } = await supabase.auth.getSession();
    set({ session: data.session, loading: false });

    if (data.session && !data.session.user.is_anonymous) {
      await get().fetchProfile();
    }

    supabase.auth.onAuthStateChange((_event, session) => {
      set({ session });
    });
  },

  signInAnonymously: async (captchaToken) => {
    set({ error: null });
    const { error } = await supabase.auth.signInAnonymously(
      captchaToken ? { options: { captchaToken } } : undefined,
    );
    if (error) set({ error: error.message });
  },

  /**
   * Creates an account — or, for a guest, turns the guest *into* one.
   *
   * `signUp` mints a new user even when an anonymous session is active, which
   * abandons the guest: a different id, so their room membership, saved name
   * and mark all belong to someone who no longer exists. `updateUser` instead
   * attaches the email and password to the anonymous user, keeping the same id.
   * The password applies immediately; the account becomes permanent when the
   * emailed link is confirmed, and until then they are simply still a guest.
   *
   * Because the id never changes, this path is safe to run from anywhere,
   * including inside a room.
   */
  register: async (email, password, captchaToken) => {
    set({ error: null });

    const { data } = await supabase.auth.getSession();
    const linking = data.session?.user.is_anonymous === true;

    const { error } = linking
      ? await supabase.auth.updateUser({ email, password })
      : await supabase.auth.signUp({
          email,
          password,
          options: captchaToken ? { captchaToken } : undefined,
        });

    if (error) {
      set({ error: error.message });
      throw error;
    }
  },

  signIn: async (email, password, captchaToken) => {
    set({ error: null });
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
      options: captchaToken ? { captchaToken } : undefined,
    });
    if (error) {
      set({ error: error.message });
      throw error;
    }
    await get().fetchProfile();
  },

  signOut: async () => {
    await supabase.auth.signOut();
    set({ session: null, displayName: null, avatarSeed: null, error: null });
  },

  resetPassword: async (email, captchaToken) => {
    set({ error: null });
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      captchaToken,
      // Where the emailed link lands. Supabase puts the recovery token in the
      // URL fragment and the client exchanges it on load, so this route only
      // has to collect the new password.
      redirectTo: `${window.location.origin}/auth/reset`,
    });
    if (error) set({ error: error.message });
  },

  updatePassword: async (password) => {
    set({ error: null });
    const { error } = await supabase.auth.updateUser({ password });
    if (error) {
      set({ error: error.message });
      throw error;
    }
  },

  deleteAccount: async () => {
    set({ error: null });
    try {
      await api("/profile/account-delete");
      await supabase.auth.signOut();
      set({ session: null, displayName: null, avatarSeed: null });
    } catch (err) {
      set({ error: err instanceof Error ? err.message : "Deletion failed" });
    }
  },

  updateProfile: async (fields) => {
    set({ error: null });
    try {
      const res = await api<IProfileGetRes>("/profile/profile-update", fields);
      set({ displayName: res.display_name, avatarSeed: res.avatar_seed });
    } catch (err) {
      set({ error: err instanceof Error ? err.message : "Update failed" });
      throw err;
    }
  },

  fetchProfile: async () => {
    try {
      const res = await api<IProfileGetRes>("/profile/profile-get");
      set({ displayName: res.display_name, avatarSeed: res.avatar_seed });
    } catch {
      // No profile yet — that's fine
    }
  },
}));
